import { Injectable } from '@angular/core';

declare const window: Window & {
  cordova?: unknown;
  PushNotification?: {
    init: (options: { android: { senderID: string }; ios?: { alert?: boolean; badge?: boolean; sound?: boolean }; windows?: object }) => { on: (event: string, callback: (data: unknown) => void) => void };
  };
};

export interface PushRegistrationResult {
  token: string;
  platform: string;
}

/**
 * Manages push notification registration and token lifecycle.
 * When running in Cordova with a push plugin (e.g. phonegap-plugin-push) installed,
 * registers for push and exposes the token. Otherwise no-ops for browser/builds without the plugin.
 *
 * Device permissions: The OS shows the permission prompt when we first call
 * requestPermissionAndRegister() (e.g. when the user enables "Push (this device)" in alert settings).
 * You do not need to call this at app startup just to get permissions.
 *
 * Optional startup: Call registerAtStartupIfAvailable() after login or in app init if you want to
 * re-establish the cached token after app restart and handle token refresh (FCM/APNs can issue new tokens).
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private cachedToken: string | null = null;
  private currentDeviceName: string | null = null;
  private registrationPromise: Promise<PushRegistrationResult | null> | null = null;

  /** True when running inside Cordova with the push plugin available. */
  get isPushAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.cordova && !!window.PushNotification;
  }

  /**
   * Request permission and register for push notifications.
   * Resolves with token and platform when successful, or null when unavailable/denied/error.
   */
  async requestPermissionAndRegister(senderId?: string): Promise<PushRegistrationResult | null> {
    if (this.registrationPromise != null) {
      return this.registrationPromise;
    }
    if (!this.isPushAvailable) {
      return Promise.resolve(null);
    }
    this.registrationPromise = new Promise<PushRegistrationResult | null>((resolve) => {
      try {
        const push = window.PushNotification!.init({
          android: { senderID: senderId || 'default-sender-id' },
          ios: { alert: true, badge: true, sound: true },
          windows: {}
        });
        push.on('registration', (data: { registrationId?: string; deviceToken?: string }) => {
          const token = data?.registrationId || data?.deviceToken;
          if (token) {
            this.cachedToken = token;
            resolve({ token, platform: this.getPlatform() });
          } else {
            resolve(null);
          }
        });
        push.on('error', () => resolve(null));
      } catch {
        resolve(null);
      }
    });
    return this.registrationPromise;
  }

  /**
   * Optional: call at app startup (e.g. after login or in root component init) to register for push
   * and restore the cached token. Use this if you want the token available without the user re-opening
   * alert settings, or to refresh the token. Does not force a permission prompt if already granted.
   */
  async registerAtStartupIfAvailable(senderId?: string): Promise<PushRegistrationResult | null> {
    return this.requestPermissionAndRegister(senderId);
  }

  /** Returns the last known push token, or null. */
  getStoredToken(): string | null {
    return this.cachedToken;
  }

  /** Device name set when user registers from User Management (e.g. "Molly's iPhone"). */
  getCurrentDeviceName(): string | null {
    return this.currentDeviceName;
  }

  setCurrentDeviceName(name: string | null): void {
    this.currentDeviceName = name;
  }

  /** Clears cached token and device name (e.g. after logout). */
  clearStoredToken(): void {
    this.cachedToken = null;
    this.currentDeviceName = null;
    this.registrationPromise = null;
  }

  private getPlatform(): string {
    if (typeof window === 'undefined' || !window.cordova) return 'unknown';
    const device = (window as unknown as { device?: { platform?: string } }).device;
    return device?.platform || 'unknown';
  }
}
